/**
 * Yards that draw themselves.
 *
 * A run is a string of yards nobody laid out by hand. Each is built from a
 * seed and a depth, and comes out as the same ASCII a hand-made level is
 * written in, so everything downstream, the parser, the physics, the
 * drawing, does not know or care where the map came from.
 *
 * The shape of a run is the point rather than the shape of any one yard:
 * clear a yard, pick which door to go through, and the reward on the door
 * is the choice. Yards get bigger and better defended the further in you
 * are, every fourth one is a boss, and between them the workshop does what
 * it always did.
 */

import { LEGEND, type Level } from "./levels.ts";

/** A small, fast, seedable generator. The same seed is the same run. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type Random = () => number;

const pick = <T>(r: Random, list: readonly T[]): T => list[Math.floor(r() * list.length)] as T;
const between = (r: Random, lo: number, hi: number) => lo + Math.floor(r() * (hi - lo + 1));

// ---- what is behind a door ---------------------------------------------------

export type RewardId = "points" | "trade" | "repair" | "shield" | "power";

export type Door = {
  reward: RewardId;
  /** What the door says, and what it means. */
  label: string;
  detail: string;
  /** A harder yard for a better reward. */
  elite: boolean;
};

const REWARDS: Record<RewardId, { label: string; detail: string }> = {
  points: { label: "Workshop", detail: "Two points to spend on the tank" },
  trade: { label: "Deal", detail: "A free trade: something for something" },
  repair: { label: "Yard hands", detail: "Patched up to full armour" },
  shield: { label: "Plating", detail: "A shield, and one more armour" },
  power: { label: "Armoury", detail: "A gun to start the next yard with" },
};

/**
 * The two or three doors out of a yard. Never two of the same, and one of
 * them may be an elite: a worse yard for a better reward.
 */
export function doorsFor(r: Random, depth: number): Door[] {
  const pool: RewardId[] = ["points", "trade", "repair", "power"];
  if (depth >= 3) pool.push("shield");
  const chosen: RewardId[] = [];
  const count = depth < 2 ? 2 : between(r, 2, 3);
  while (chosen.length < count && chosen.length < pool.length) {
    const id = pick(r, pool);
    if (!chosen.includes(id)) chosen.push(id);
  }
  const eliteAt = depth >= 2 && r() < 0.5 ? between(r, 0, chosen.length - 1) : -1;
  return chosen.map((id, i) => ({
    reward: id,
    label: REWARDS[id].label,
    detail: REWARDS[id].detail,
    elite: i === eliteAt,
  }));
}

// ---- the yard ----------------------------------------------------------------

export type YardKind = "yard" | "elite" | "boss";

/** What each sort of enemy is worth against the yard's budget. */
const COST: Record<string, number> = { D: 2, E: 3, T: 3, H: 4, M: 4, G: 6 };

const DISTRICTS = [
  { name: "The Yards", block: "=", enemies: ["E", "E", "D", "T"] },
  { name: "The Works", block: "#", enemies: ["E", "H", "D", "T", "M"] },
  { name: "The Fort", block: "#", enemies: ["H", "G", "T", "M", "E"] },
  { name: "The Hill", block: "#", enemies: ["G", "H", "M", "T", "H"] },
];

const districtOf = (depth: number) => DISTRICTS[Math.min(DISTRICTS.length - 1, Math.floor((depth - 1) / 4))] as typeof DISTRICTS[number];

class Grid {
  readonly cells: string[][];
  constructor(readonly w: number, readonly h: number) {
    this.cells = Array.from({ length: h }, () => Array.from({ length: w }, () => "."));
    for (let x = 0; x < w; x++) {
      this.cells[0]![x] = "W";
      this.cells[h - 1]![x] = "W";
    }
    for (let y = 0; y < h; y++) {
      this.cells[y]![0] = "W";
      this.cells[y]![w - 1] = "W";
    }
  }
  at(x: number, y: number): string {
    return this.cells[y]?.[x] ?? "W";
  }
  set(x: number, y: number, ch: string): void {
    if (y > 0 && y < this.h - 1 && x > 0 && x < this.w - 1) this.cells[y]![x] = ch;
  }
  /** Free means bare ground with nothing of interest within `pad`. */
  free(x: number, y: number, pad = 0): boolean {
    for (let dy = -pad; dy <= pad; dy++) {
      for (let dx = -pad; dx <= pad; dx++) {
        if (this.at(x + dx, y + dy) !== ".") return false;
      }
    }
    return true;
  }
  rows(): string[] {
    return this.cells.map((row) => row.join(""));
  }
}

/** Somewhere free, given enough tries; null if the yard is too crowded. */
function spot(g: Grid, r: Random, pad = 0, avoid?: { x: number; y: number; away: number }): { x: number; y: number } | null {
  for (let i = 0; i < 120; i++) {
    const x = between(r, 2, g.w - 3);
    const y = between(r, 2, g.h - 3);
    if (!g.free(x, y, pad)) continue;
    if (avoid && Math.hypot(x - avoid.x, y - avoid.y) < avoid.away) continue;
    return { x, y };
  }
  return null;
}

/**
 * Build one yard. Blocks of cover, a street if the dice say so, a garrison
 * bought with a budget that grows with depth, and factories that keep
 * sending more until they are knocked down.
 */
export function generateYard(seed: number, depth: number, kind: YardKind = "yard"): Level {
  const r = rng(seed);
  const district = districtOf(depth);
  const boss = kind === "boss";
  const elite = kind === "elite";

  // Tall, like the campaign: you start at the bottom and work upwards.
  const w = Math.min(24, between(r, 20, 22) + Math.floor(depth / 4));
  const h = Math.min(40, between(r, 30, 34) + Math.floor(depth / 2));
  const g = new Grid(w, h);

  // A ring of clear ground just inside the wall, so there is always a way
  // round whatever ends up in the middle.
  const inner = { x0: 3, y0: 3, x1: w - 4, y1: h - 4 };

  // Cover: rectangular blocks, only in the middle ground.
  const blocks = between(r, 6, 10) + (boss ? 2 : 0);
  for (let i = 0; i < blocks; i++) {
    const bw = between(r, 2, 5);
    const bh = between(r, 2, 4);
    const x = between(r, inner.x0, Math.max(inner.x0, inner.x1 - bw));
    const y = between(r, inner.y0, Math.max(inner.y0, inner.y1 - bh));
    const ch = r() < 0.25 ? "-" : r() < 0.4 ? "=" : district.block;
    for (let dy = 0; dy < bh; dy++) {
      for (let dx = 0; dx < bw; dx++) g.set(x + dx, y + dy, ch);
    }
  }

  // A street, sometimes: two lanes across the yard, with traffic on it.
  if (!boss && r() < 0.45) {
    const across = r() < 0.5;
    if (across) {
      const y = between(r, 4, h - 6);
      for (let x = 1; x < w - 1; x++) {
        g.set(x, y, "~");
        g.set(x, y + 1, "~");
      }
      g.set(between(r, 4, w - 6), y, "V");
      g.set(between(r, 4, w - 6), y + 1, "V");
    } else {
      const x = between(r, 4, w - 6);
      for (let y = 1; y < h - 1; y++) {
        g.set(x, y, "~");
        g.set(x + 1, y, "~");
      }
      g.set(x, between(r, 4, h - 6), "V");
      g.set(x + 1, between(r, 4, h - 6), "V");
    }
  }

  // The player at the bottom in the middle, facing up; the way out along
  // the top, off to one side so it is never a straight run.
  const mid = Math.floor(w / 2);
  const player = { x: mid, y: h - 3 };
  const exit = { x: r() < 0.5 ? between(r, 2, mid - 4) : between(r, mid + 4, w - 3), y: 2 };
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      g.set(player.x + dx, player.y + dy, ".");
      g.set(exit.x + dx, exit.y + dy, ".");
    }
  }
  g.set(player.x, player.y, "P");
  g.set(exit.x, exit.y, "X");

  // Factories. They are the reason a yard does not simply empty out.
  const works = boss ? 1 : Math.min(3, 1 + Math.floor(depth / 3)) + (elite ? 1 : 0);
  for (let i = 0; i < works; i++) {
    const at = spot(g, r, 1, { ...player, away: 8 });
    if (at) g.set(at.x, at.y, "F");
  }

  // The garrison, bought with a budget.
  let budget = Math.round(5 + depth * 2.4) * (elite ? 1.5 : 1);
  if (boss) {
    const at = spot(g, r, 2, { ...player, away: 10 }) ?? { x: Math.floor(w / 2), y: Math.floor(h / 2) };
    g.set(at.x, at.y, "Z");
    budget = Math.round(budget * 0.5);
  }
  let guard = 0;
  while (budget > 1 && guard++ < 60) {
    const ch = pick(r, district.enemies);
    const cost = COST[ch] ?? 3;
    if (cost > budget) continue;
    const at = spot(g, r, 0, { ...player, away: 7 });
    if (!at) break;
    g.set(at.x, at.y, ch);
    budget -= cost;
  }

  // Things lying about, and something to pick up.
  for (let i = 0, n = between(r, 10, 18); i < n; i++) {
    const at = spot(g, r);
    if (at) g.set(at.x, at.y, pick(r, ["B", "c", "c", "v"]));
  }
  for (let i = 0, n = between(r, 1, 2); i < n; i++) {
    const at = spot(g, r, 1);
    if (at) g.set(at.x, at.y, pick(r, ["1", "2", "3"]));
  }
  if (depth >= 3 && r() < 0.5) {
    const at = spot(g, r, 1);
    if (at) g.set(at.x, at.y, r() < 0.5 ? "+" : "@");
  }

  const rows = g.rows();
  const enemies = rows.join("").split("").filter((ch) => COST[ch] || ch === "Z").length;
  const name = boss ? `${district.name}: the last yard` : elite ? `${district.name}: a bad corner` : district.name;
  return {
    name,
    objective: boss
      ? "One of theirs is bigger than the rest"
      : works > 0
      ? `Knock down the ${works === 1 ? "works" : "works, all of them"} and get out`
      : "Clear it out and get out",
    par: Math.round(28 + enemies * 5 + works * 8),
    map: rows,
  };
}

/** Every character a generated yard may use, for the test that pins it. */
export const GENERATED = `.W#=-BcvPTEZSX123+@~VFHGMD`;
export const LEGAL = new Set(LEGEND.split(""));
